import { useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Input,
  DropDownMenu,
  Checkbox,
  TableRow,
  TableCell,
  Typography,
} from '@safedns/skydns-ui-react-lib';

import TableRowButtons from './TableRowButtons';

import { updatedLocalDomain } from '../../store/localDomainsReducer';
import { validateLocalDomainName } from '../../utils/validateLocalDomainName';

import ModalContext from '../../contexts/modal-context';

import {
  useGetUserQuery,
  useGetDomainControllersQuery,
  useEditLocalDomainMutation,
} from '../../services/ADapi';
import { notificationSuccess } from '../../store/notificationReducer';

/**
 *
 * @param {string | number} id
 * @param {string} name
 * @param {number[]} domainControllerIds
 * @returns
 */

const LocalDomainTableRow = ({ id, name, controllerIds }) => {
  const dispatch = useDispatch();
  const [isEditable, setEditableState] = useState();
  const [isFormValid, setFormState] = useState(false);
  const [errors, setErrors] = useState({});
  const [domainControllerIds, setDomainControllerIds] = useState([]);
  const [localDomainName, setLocalDomainName] = useState(name);
  const { setDeleteLocalDomainModalState } = useContext(ModalContext);

  const {
    data: { user_id },
  } = useGetUserQuery();
  const { data: controllers = [] } = useGetDomainControllersQuery(user_id);
  const [editLocalDomain] = useEditLocalDomainMutation();

  useEffect(() => {
    const selectedDomainControllers =
      controllers?.filter((controller) =>
        controllerIds?.includes(controller.id)
      ) || [];

    setDomainControllerIds(selectedDomainControllers);
  }, [controllerIds, controllers]);

  useEffect(() => {
    const localDomainNameValid = validateLocalDomainName(localDomainName);

    setFormState(localDomainNameValid);
  }, [localDomainName]);

  const onToggleRemoveModal = () => {
    dispatch(
      updatedLocalDomain({
        id: id,
        name: name,
      })
    );

    return setDeleteLocalDomainModalState(true);
  };

  const switchToEditMode = () => {
    setEditableState(true);
  };

  const declineEditMode = () => {
    setEditableState(false);
  };

  const localDomainNameHandler = (e) => {
    const errorsCopy = { ...errors };
    delete errorsCopy.domain;
    setErrors(errorsCopy);
    setLocalDomainName(e.target.value);
  };

  const handleControllerChange = (controller) => {
    setDomainControllerIds((prev) => {
      const isAlreadySelected = prev.some((c) => c?.id === controller.id);
      if (isAlreadySelected) {
        return prev.filter((c) => c?.id !== controller.id);
      } else {
        return [...prev, controller];
      }
    });
  };

  const editLocalDomainHandler = async () => {
    const ids = domainControllerIds.map((c) => c.id);

    try {
      await editLocalDomain({
        user_id,
        localDomain: {
          id,
          domain: localDomainName,
          controller_ids: ids,
        },
      }).unwrap();

      dispatch(
        notificationSuccess({
          isNotification: true,
          type: 'success',
          message: 'Изменения сохранены',
        })
      );
      setErrors({});
      setEditableState(false);
    } catch (errors) {
      setErrors(errors);
    }
  };

  return (
    <TableRow>
      <TableCell>
        {isEditable ? (
          <Input
            value={localDomainName}
            placeholder="Введите название локального домена"
            onChange={localDomainNameHandler}
          />
        ) : (
          name
        )}

        {errors.domain && isEditable ? (
          <Typography variant="body2" color="#e08d8d">
            {errors.domain}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        {isEditable ? (
          <DropDownMenu
            id="selectDomainController"
            name={
              domainControllerIds
                ?.map((c) => c?.name)
                .filter(Boolean)
                .join(', ') || 'Не выбрано'
            }
            sx={{ maxWidth: '360px' }}
          >
            {controllers.map((controller) => (
              <Checkbox
                key={controller.id}
                id={controller.id}
                size="small"
                value={controller.name}
                label={controller.name}
                isChecked={domainControllerIds?.some(
                  (c) => c?.id === controller.id
                )}
                onChange={() => handleControllerChange(controller)}
                sx={{ padding: 8 }}
              />
            ))}
          </DropDownMenu>
        ) : (
          controllerIds.length
        )}
        {errors.controller_ids && isEditable ? (
          <Typography variant="body2" color="#e08d8d">
            {errors.controller_ids}
          </Typography>
        ) : null}
      </TableCell>
      <TableRowButtons
        isEditable={isEditable}
        onToggleRemoveModal={onToggleRemoveModal}
        switchToEditMode={switchToEditMode}
        declineEditMode={declineEditMode}
        onSave={editLocalDomainHandler}
        isSaveDisabled={!isFormValid}
      />
    </TableRow>
  );
};

export default LocalDomainTableRow;
